import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * @module ErrorBoundary
 * @description
 * A React Error Boundary component that catches JavaScript errors in child components,
 * logs them, and displays a fallback UI instead of crashing the entire application.
 *
 * ## Architecture
 * Uses React's built-in error boundary lifecycle methods:
 * - getDerivedStateFromError: Updates state to render fallback UI
 * - componentDidCatch: Logs error information for debugging
 *
 * ## Responsibilities
 * - Catch rendering errors in child components
 * - Display graceful fallback UI instead of white screen
 * - Log errors for admin/debugging purposes
 * - Allow custom fallback UIs per widget
 *
 * ## Usage
 * ```tsx
 * <ErrorBoundary fallback={<div>Widget unavailable</div>}>
 *   <RiskyComponent />
 * </ErrorBoundary>
 * ```
 */

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: React.ReactNode;
  /** Custom fallback UI to display on error */
  fallback?: React.ReactNode;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Component name for logging */
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const componentName = this.props.componentName || 'Desconocido';

    // Report to Sentry
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: { component: componentName },
    });

    // Log error for admin/debugging
    if (import.meta.env.DEV) {
      console.error(`[ErrorBoundary:${componentName}] Captured error:`, error);
      console.error(`[ErrorBoundary:${componentName}] Component stack:`, errorInfo.componentStack);
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback or default
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-amber-700 font-medium">Widget no disponible</p>
          </div>
          <p className="text-amber-600 text-sm mt-1">
            Este componente no está disponible temporalmente
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

/**
 * Pre-built fallback UIs for common widgets
 */
export const MapsFallback: React.FC = () => (
  <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl min-h-[300px] flex items-center justify-center">
    <div className="text-center">
      <svg className="w-12 h-12 text-amber-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
      <p className="text-amber-700 font-semibold">Servicio de mapas no disponible temporalmente</p>
      <p className="text-amber-600 text-sm mt-1">Intente recargar la página</p>
    </div>
  </div>
);

export const KPIFallback: React.FC = () => (
  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl h-40 flex items-center justify-center">
    <div className="text-center">
      <svg className="w-8 h-8 text-gray-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <p className="text-gray-600 text-sm">KPI no disponible</p>
    </div>
  </div>
);

export const ChartFallback: React.FC = () => (
  <div className="p-6 bg-gray-50 border border-gray-200 rounded-xl min-h-[200px] flex items-center justify-center">
    <div className="text-center">
      <svg className="w-10 h-10 text-gray-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
      </svg>
      <p className="text-gray-600 text-sm">Gráfico no disponible temporalmente</p>
    </div>
  </div>
);

export const TableFallback: React.FC = () => (
  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
    <div className="flex items-center space-x-2 text-gray-600">
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      <p className="text-sm">Datos no disponibles temporalmente</p>
    </div>
  </div>
);
